/**
 * useDisclosure — page-shell's ONE disclosure primitive (OWNER-RULINGS-R7 W-R7-DISCLOSURE,
 * R7-16 / Q-R7ORD-6): a boolean shown / hidden state with STABLE `open`, `close` and `toggle`.
 *
 * Replaces the hand-rolled triplet
 *
 *     const [isOpen, setIsOpen] = useState(false);
 *     const open  = useCallback(() => setIsOpen(true),  []);
 *     const close = useCallback(() => setIsOpen(false), []);
 *
 * which the fleet census (`CRITICAL-FIXES/owner-rulings-r7-followons/tools/disclosureTripletCensus.cjs`,
 * 2026-09-24) measured **31 times across eight web apps** — dialogs, drawers, confirms, a
 * tooltip, a zoom — plus 33 one-sided pairs and 16 toggles of the same state shape. It was
 * promoted from pos-web's `features/orders/hooks/useDialogState` (L-ORD, R6-87), where the
 * nested-hook form had already been the fix for `web/excessive-hooks-usage`: a hook TREE, not a
 * flat aggregator (STRUCT-DECOMP-001 §2).
 *
 * Every callback is stable for the life of the component (the setter is stable; each callback
 * closes over nothing else), so a child receiving `open` / `close` as props never re-renders for
 * a new identity. ⚠️ That is a BEHAVIOUR for a caller that used plain, per-render functions
 * before: a child effect keyed on the callback stops re-running. Convert such a site knowingly.
 *
 * `defaultOpen` is read ONCE, on mount — the `useState` initial-value contract; a later change
 * does not re-initialise (`useDisclosure.test.js` pins it). A caller that must follow a prop
 * owns that state itself (a controlled component), which is not this primitive's job.
 *
 * @module hooks/useDisclosure
 */

import { useCallback, useState } from 'react';

/**
 * @param {Object} [options]
 * @param {boolean} [options.defaultOpen=false] - Initial state, read once on mount.
 * @returns {{isOpen: boolean, open: () => void, close: () => void, toggle: () => void}}
 *   The state and three stable callbacks.
 */
export function useDisclosure({ defaultOpen = false } = {}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((current) => !current), []);
  return { isOpen, open, close, toggle };
}

export default useDisclosure;
